import { memo, useMemo, useRef, type MouseEvent as ReactMouseEvent } from "react";
import type { Message } from "../hooks/useChat";
import { Avatar } from "./Avatar";
import { AuthImage } from "./AuthImage";
import { ReactionBar, type ReactionGroup } from "./ReactionBar";
import { LinkPreview } from "./LinkPreview";
import { MessageMarkdown } from "./MessageMarkdown";
import { AudioPlayer } from "./AudioPlayer";
import { TtsButton } from "./TtsButton";
import { FilePreview } from "./FilePreview";
import { safeMessageText, humanizeFileTokens } from "../lib/safeMessageText";
import { parseFileAttachments } from "../lib/file-tokens";
import { Pencil, Check, CheckCheck, MoreHorizontal } from "lucide-react";
import styles from "./ChatView.module.css";

// ── Message Bubble ──

export interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showAvatar: boolean;
  grouped?: boolean;
  isBotMessage?: boolean;
  isRead?: boolean;
  replyTo: Message | null | undefined;
  /** Position ratio 0..1 within the message list (0 = top, 1 = bottom) */
  positionRatio?: number;
  onContextMenu: (e: ReactMouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
  onReactionToggle: (emoji: string) => void;
  onImageClick?: (blobUrl: string) => void;
  onMenuOpen?: (x: number, y: number) => void;
}

/** Telegram-style flowing gradient: interpolate color based on viewport Y position.
 *  Top of screen = purple/magenta, bottom = blue. */
const GRADIENT_STOPS = [
  [155, 89, 182],   // #9B59B6 — lila-magenta (oben)
  [139, 82, 191],   // #8B52BF — lila
  [108, 94, 207],   // #6C5ECF — violett
  [98, 107, 215],   // #626BD7 — blau-indigo
  [91, 127, 225],   // #5B7FE1 — blau (unten)
] as const;

function getBubbleColor(ratio: number): string {
  const t = Math.max(0, Math.min(1, ratio));
  const segments = GRADIENT_STOPS.length - 1;
  const segment = Math.min(Math.floor(t * segments), segments - 1);
  const localT = (t * segments) - segment;
  const from = GRADIENT_STOPS[segment]!;
  const to = GRADIENT_STOPS[segment + 1]!;
  const r = Math.round(from[0] + (to[0] - from[0]) * localT);
  const g = Math.round(from[1] + (to[1] - from[1]) * localT);
  const b = Math.round(from[2] + (to[2] - from[2]) * localT);
  return `rgb(${r}, ${g}, ${b})`;
}

export { getBubbleColor };

export const MessageBubble = memo(function MessageBubble({
  message,
  isOwn,
  showAvatar,
  grouped,
  isBotMessage,
  isRead,
  replyTo,
  positionRatio: _positionRatio = 0.5,
  onContextMenu,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  onReactionToggle,
  onImageClick,
  onMenuOpen,
}: MessageBubbleProps) {
  if (message.deletedAt) {
    return (
      <div className={`${styles.messageRow} ${isOwn ? styles.own : ""}`}>
        <div className={styles.deleted}>
          <em>Nachricht gelöscht</em>
        </div>
      </div>
    );
  }

  const rawText = safeMessageText(message);
  const { text: displayText, files } = parseFileAttachments(rawText);
  const firstFile = files[0] ?? null;

  const time = new Date(message.createdAt).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Triple-click should select only the message text, not the floating
  // timestamp/status row. The browser's default triple-click selects the
  // smallest enclosing block — here that's the entire .messageRow, which
  // includes .time. We narrow on mouseup, but ONLY when the user did not
  // drag (movement <5px). If they triple-clicked AND dragged to extend,
  // their drag-selection wins and we leave it alone.
  const messageTextRef = useRef<HTMLSpanElement | null>(null);
  const tripleClickPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleMessageTextMouseDown = (e: ReactMouseEvent<HTMLSpanElement>) => {
    tripleClickPosRef.current = e.detail === 3 ? { x: e.clientX, y: e.clientY } : null;
  };
  const handleMessageTextMouseUp = (e: ReactMouseEvent<HTMLSpanElement>) => {
    const start = tripleClickPosRef.current;
    tripleClickPosRef.current = null;
    if (!start || e.detail !== 3) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.sqrt(dx * dx + dy * dy) > 5) return; // user dragged — keep their selection
    const node = messageTextRef.current;
    if (!node) return;
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Map Message.reactions to ReactionBar's ReactionGroup format
  const reactions = useMemo<ReactionGroup[]>(
    () => (message.reactions ?? []).map((r) => ({
      emoji: r.emoji,
      count: r.count,
      userReacted: r.userReacted,
    })),
    [message.reactions],
  );

  return (
    <div
      className={`${styles.messageRow} ${isOwn ? styles.own : ""}`}
      onContextMenu={onContextMenu}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      {!isOwn && showAvatar ? (
        <Avatar name={message.senderName ?? "?"} size="xs" isBot={isBotMessage} />
      ) : !isOwn ? (
        <div className={styles.avatarSpacer} />
      ) : null}

      <div className={`${styles.bubble} ${isOwn ? styles.ownBubble : styles.otherBubble} ${grouped ? styles.groupedBubble : ""} ${grouped && isOwn ? styles.groupedBubbleOwn : ""} ${grouped && !isOwn ? styles.groupedBubbleOther : ""} ${(message.contentType === "image" || (firstFile && firstFile.contentType.startsWith("image/"))) ? styles.imageBubble : ""}`}>
        {!isOwn && showAvatar && !grouped && message.senderName && (
          <span className={styles.senderName}>
            {message.senderName}

          </span>
        )}

        {/* Reply reference */}
        {replyTo && (
          <div className={styles.replyRef}>
            <div className={styles.replyRefBar} />
            <div className={styles.replyRefContent}>
              <span className={styles.replyRefName}>
                {replyTo.senderName ?? "Unbekannt"}
              </span>
              <span className={styles.replyRefText}>
                {humanizeFileTokens(safeMessageText(replyTo)).slice(0, 60)}
              </span>
            </div>
          </div>
        )}

        {/* Direct image message (contentType === "image") */}
        {files.length === 0 && message.contentType === "image" && rawText && (
          <div className={styles.imageAttachment}>
            <AuthImage
              src={rawText.startsWith("/") ? rawText : `/files/${rawText}`}
              alt="Bild"
              className={styles.attachedImage}
              onClick={(blobUrl) => onImageClick?.(blobUrl)}
            />
          </div>
        )}

        {/* File attachments — eine Nachricht kann mehrere Dateien tragen */}
        {files.map((file) => {
          if (file.contentType.startsWith("image/")) {
            return (
              <div key={file.fileId} className={styles.imageAttachment}>
                <AuthImage
                  src={`/files/${file.fileId}`}
                  alt={file.filename}
                  className={styles.attachedImage}
                  encryptionKey={file.encryptionKey}
                  encryptionIv={file.encryptionIv}
                  originalMime={file.encryptionKey ? file.contentType : undefined}
                  onClick={(blobUrl) => onImageClick?.(blobUrl)}
                />
              </div>
            );
          }
          if (file.contentType.startsWith("audio/")) {
            return (
              <AudioPlayer
                key={file.fileId}
                fileId={file.fileId}
                filename={file.filename}
                encryptionKey={file.encryptionKey}
                encryptionIv={file.encryptionIv}
                originalMime={file.encryptionKey ? file.contentType : undefined}
              />
            );
          }
          return (
            <FilePreview
              key={file.fileId}
              fileId={file.fileId}
              contentType={file.contentType}
              filename={file.filename}
              encryptionKey={file.encryptionKey}
              encryptionIv={file.encryptionIv}
            />
          );
        })}

        {message.fallbackMode === "plaintext" && (
          <span
            title="Diese Nachricht wurde nicht Ende-zu-Ende entschlüsselt — Übergangs-Failsafe (Server-Plaintext) wurde verwendet."
            style={{
              display: "inline-block",
              fontSize: "0.7rem",
              padding: "1px 6px",
              marginBottom: "4px",
              borderRadius: "999px",
              background: "rgba(255, 165, 0, 0.18)",
              color: "#c47600",
              border: "1px solid rgba(196, 118, 0, 0.35)",
              fontWeight: 500,
              letterSpacing: "0.01em",
            }}
          >
            ⚠ Übergangs-Modus
          </span>
        )}

        {displayText && (
          <span
            className={styles.messageText}
            ref={messageTextRef}
            onMouseDown={handleMessageTextMouseDown}
            onMouseUp={handleMessageTextMouseUp}
          >
            <MessageMarkdown text={displayText} />
            {/* Invisible spacer reserves room so text doesn't flow under the time */}
            <span className={`${styles.timeSpacer} ${isOwn ? styles.withStatus : ""}`} />
          </span>
        )}

        {/* Link Previews */}
        {displayText && <LinkPreviews text={displayText} />}

        <span className={styles.time}>
          {displayText && <TtsButton text={displayText} />}
          {message.editedAt && <><Pencil size={12} />{" "}</>}
          {time}
          {isOwn && (
            <span className={`${styles.readStatus} ${isRead ? styles.read : ""}`}>
              {isRead
                ? <CheckCheck size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                : <Check size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
              }
            </span>
          )}
        </span>

        {/* Reactions */}
        {reactions.length > 0 && (
          <ReactionBar
            reactions={reactions}
            onToggle={(emoji) => onReactionToggle(emoji)}
          />
        )}
      </div>
      <button
        type="button"
        className={styles.messageActionBtn}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          onMenuOpen?.(rect.left, rect.bottom + 4);
        }}
        aria-label="Nachrichtenoptionen"
        tabIndex={-1}
      >
        <MoreHorizontal size={16} />
      </button>
    </div>
  );
});

// ── Helper: extract URLs and render link previews ──
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

function LinkPreviews({ text }: { text: string }) {
  const urls = useMemo(() => {
    const matches = text.match(URL_REGEX);
    return matches ? [...new Set(matches)].slice(0, 2) : [];
  }, [text]);

  if (urls.length === 0) return null;

  return (
    <>
      {urls.map((url) => (
        <LinkPreview key={url} url={url} />
      ))}
    </>
  );
}
